/**
 * useRouter — the History-API binding of `router.ts`.
 *
 * `<RouterProvider>` owns the current route, listens to `popstate` (the browser Back button
 * works, spec §8.12) and exposes `navigate(path, {replace})`. Links use `<Link>` so a plain
 * click is intercepted while ⌘/Ctrl-click, middle-click and "open in new tab" still work.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { matchRoute, type Route } from '../router.ts';

interface RouterValue {
  route: Route;
  navigate: (path: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

function currentPath(): string {
  if (typeof location === 'undefined') return '/';
  return location.pathname + location.search;
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(currentPath);

  useEffect(() => {
    const onPop = () => setPath(currentPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: string, options?: { replace?: boolean }) => {
    if (next === currentPath()) {
      setPath(next);
      return;
    }
    if (options?.replace) history.replaceState(null, '', next);
    else history.pushState(null, '', next);
    setPath(next);
  }, []);

  const value = useMemo<RouterValue>(() => ({ route: matchRoute(path), navigate }), [path, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error('RouterProvider missing');
  return value;
}

export function useRoute(): Route {
  return useRouter().route;
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; replace?: boolean };

/** An in-app link: intercepts plain left clicks, leaves modified clicks to the browser. */
export function Link({ to, replace, onClick, children, ...rest }: LinkProps) {
  const { navigate } = useRouter();
  return (
    <a
      href={to}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(to, { replace });
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
